import { Injectable } from '@angular/core';
import { toCanvas } from 'html-to-image';
import { jsPDF } from 'jspdf';
import { AttendanceReportRow, AttendanceStatus } from './attendance.service';

const ARABIC_FONT = '"Noto Naskh Arabic", "Segoe UI", "Tahoma", "Geeza Pro", Arial, sans-serif';
const PAGE_WIDTH_PX = 794;
const PAGE_HEIGHT_PX = 1123;
const LANDSCAPE_PAGE_WIDTH_PX = 1123;
const LANDSCAPE_PAGE_HEIGHT_PX = 794;
const INK = '#20242b';
const MUTED = '#5b6675';
const LINE = '#d0d5dd';
const HEAD_BG = '#f2f4f7';

export interface AttendancePdfLabels {
  title: string;
  date: string;
  classLabel: string;
  total: string;
  attended: string;
  late: string;
  absent: string;
  attendanceRate: string;
  absenceRate: string;
  name: string;
  checkIn: string;
  checkOut: string;
  timing: string;
  emptySection: string;
  noClass: string;
  allClasses?: string;
  classesWithAbsent?: string;
  statusEarly: string;
  statusOnTime: string;
  statusLate: string;
}

export interface AttendancePdfClassReport {
  classId: string;
  className: string;
  total: number;
  attended: AttendanceReportRow[];
  late: AttendanceReportRow[];
  absent: AttendanceReportRow[];
}

export interface AttendancePdfExportInput {
  date: string;
  schoolName: string;
  headName?: string;
  sectionHeadName?: string;
  isArabic: boolean;
  lateEnabled?: boolean;
  /** When true, only the absent section is rendered (summary still uses full class totals). */
  absentOnly?: boolean;
  /**
   * One shared report with class sections stacked (not a separate report per class).
   * Used for all-classes absent PDF.
   */
  combined?: boolean;
  labels: AttendancePdfLabels;
  classes: AttendancePdfClassReport[];
}

export interface AttendanceRangePdfLabels {
  title: string;
  dateRange: string;
  classLabel: string;
  totalStudents: string;
  dayCount: string;
  attended: string;
  late: string;
  absent: string;
  attendanceRate: string;
  absenceRate: string;
  name: string;
  presentDays: string;
  absentDays: string;
  lateDays: string;
  rate: string;
  emptySection: string;
  noClass: string;
  markPresent: string;
  markAbsent: string;
  markLate: string;
}

export interface AttendanceRangePdfRow {
  studentName: string;
  presentDays: number;
  absentDays: number;
  lateDays: number;
  attendanceRate: number;
  dayMarks: Record<string, 'present' | 'absent' | 'late' | 'holiday'>;
}

export interface AttendanceRangePdfClassReport {
  classId: string;
  className: string;
  rows: AttendanceRangePdfRow[];
}

export interface AttendanceRangePdfExportInput {
  from: string;
  to: string;
  days: string[];
  holidayWeekdays?: number[];
  holidayDates?: string[];
  lateEnabled?: boolean;
  schoolName: string;
  headName?: string;
  sectionHeadName?: string;
  isArabic: boolean;
  labels: AttendanceRangePdfLabels;
  classes: AttendanceRangePdfClassReport[];
  summary: {
    totalStudents: number;
    dayCount: number;
    attendanceRate: number;
    absenceRate: number;
    late: number;
  };
}

const SHEET_CSS = `
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    background: #ffffff;
    color: ${INK};
    font-family: ${ARABIC_FONT};
  }
  #sheet {
    width: ${PAGE_WIDTH_PX}px;
    min-height: ${PAGE_HEIGHT_PX}px;
    padding: 28px 32px;
    background: #ffffff;
    color: ${INK};
    font-family: ${ARABIC_FONT};
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
  }
  #sheet.landscape {
    width: ${LANDSCAPE_PAGE_WIDTH_PX}px;
    min-height: ${LANDSCAPE_PAGE_HEIGHT_PX}px;
    padding: 16px 20px;
  }
  .sheet-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  #sheet.landscape .sheet-head {
    margin-bottom: 16px;
    padding-bottom: 10px;
  }
  #sheet.landscape .sheet-logos img {
    height: 42px;
  }
  #sheet.landscape .sheet-school {
    font-size: 15px;
  }
  #sheet.landscape .sheet-title {
    font-size: 14px;
    margin-top: 4px;
  }
  #sheet.landscape .sheet-meta {
    margin-top: 4px;
    font-size: 12px;
  }
  #sheet.landscape .summary {
    gap: 8px;
    margin: 0 0 12px;
  }
  #sheet.landscape .summary-item {
    padding: 6px 8px;
    min-width: 90px;
    flex: 1 1 90px;
  }
  #sheet.landscape .summary-item .label {
    font-size: 11px;
    margin-bottom: 2px;
  }
  #sheet.landscape .summary-item .value {
    font-size: 16px;
  }
  #sheet.landscape .section {
    margin-top: 0;
  }
  #sheet.landscape .section-title {
    font-size: 12px;
    margin-bottom: 6px;
    padding: 4px 8px;
  }
  #sheet.landscape table.report {
    font-size: 11px;
  }
  #sheet.landscape table.report th,
  #sheet.landscape table.report td {
    padding: 4px 5px;
  }
  #sheet.landscape table.report th.mark,
  #sheet.landscape table.report td.mark {
    width: 30px;
    min-width: 30px;
    max-width: 34px;
    padding: 3px 1px;
    font-size: 10px;
    white-space: nowrap;
    line-height: 1.15;
  }
  #sheet.landscape table.report th.mark {
    font-size: 9px;
  }
  #sheet.landscape table.report th.metric,
  #sheet.landscape table.report td.metric {
    text-align: center;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
  #sheet.landscape .signature-wrap {
    margin-top: auto;
    padding: 16px 0 4px;
  }
  #sheet.landscape .signature-label {
    margin-bottom: 14px;
    font-size: 12px;
  }
  #sheet.landscape .signature-name {
    font-size: 13px;
  }
  .sheet-head {
    display: flex;
    flex-direction: row;
    direction: ltr;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 18px;
    border-bottom: 2px solid ${LINE};
    padding-bottom: 14px;
  }
  .sheet-logos {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }
  .sheet-logos img {
    height: 58px;
    width: auto;
    display: block;
  }
  .sheet-head-text {
    flex: 1;
    text-align: right;
  }
  .sheet-school {
    font-size: 18px;
    font-weight: 700;
    color: ${INK};
  }
  .sheet-title {
    font-size: 16px;
    font-weight: 700;
    margin-top: 6px;
    color: ${INK};
  }
  .sheet-meta {
    margin-top: 8px;
    font-size: 13px;
    color: ${MUTED};
  }
  .summary {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin: 14px 0 18px;
  }
  .summary-item {
    flex: 1 1 110px;
    min-width: 110px;
    border: 1px solid ${LINE};
    border-radius: 8px;
    padding: 10px 12px;
    text-align: center;
  }
  .summary-item .label {
    display: block;
    font-size: 12px;
    color: ${MUTED};
    font-weight: 600;
    margin-bottom: 4px;
  }
  .summary-item .value {
    font-size: 20px;
    font-weight: 750;
  }
  .summary-item.total .value { color: ${INK}; }
  .summary-item.attended .value { color: #027a48; }
  .summary-item.late .value { color: #b42318; }
  .summary-item.absent .value { color: #b42318; }
  .summary-item.rate-ok .value { color: #027a48; }
  .summary-item.rate-bad .value { color: #b42318; }
  .section {
    margin-top: 16px;
    page-break-inside: avoid;
  }
  .section-title {
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 8px;
    padding: 6px 10px;
    border-radius: 6px;
  }
  .section-title.attended { background: #ecfdf3; color: #027a48; }
  .section-title.absent { background: #fef3f2; color: #b42318; }
  .class-block {
    margin-top: 10px;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .class-block:first-child {
    margin-top: 0;
  }
  .class-block-title {
    font-size: 13px;
    font-weight: 750;
    margin: 0 0 4px;
    padding: 6px 8px;
    background: #f2f4f7;
    color: ${INK};
    border-radius: 6px;
    border-inline-start: 4px solid #b42318;
  }
  .class-block-title .count {
    color: #b42318;
    font-weight: 700;
  }
  .absent-list {
    margin: 0;
    padding: 0;
    list-style: none;
    columns: 2;
    column-gap: 16px;
    font-size: 12px;
  }
  .absent-list li {
    break-inside: avoid;
    page-break-inside: avoid;
    padding: 3px 0;
    border-bottom: 1px solid #eef1f5;
    display: flex;
    gap: 6px;
  }
  .absent-list .num {
    flex: 0 0 1.5rem;
    color: ${MUTED};
    font-variant-numeric: tabular-nums;
  }
  .absent-list .name {
    flex: 1;
    min-width: 0;
  }
  table.report {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    table-layout: fixed;
  }
  table.report th,
  table.report td {
    border: 1px solid ${LINE};
    padding: 7px 8px;
    text-align: start;
    vertical-align: middle;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  table.report th {
    background: ${HEAD_BG};
    color: ${MUTED};
    font-weight: 700;
  }
  table.report td.num,
  table.report th.num {
    width: 36px;
    text-align: center;
    color: ${MUTED};
  }
  table.report td.name,
  table.report th.name {
    width: auto;
  }
  table.report td.time,
  table.report th.time {
    width: 72px;
    text-align: center;
  }
  table.report td.timing,
  table.report th.timing {
    width: 88px;
    text-align: center;
  }
  table.report tr.row-late td {
    background: #ffedd5;
  }
  .continued-note {
    margin: 0 0 10px;
    font-size: 11px;
    color: ${MUTED};
    font-weight: 600;
  }
  table.report td.mark,
  table.report th.mark {
    text-align: center;
    font-weight: 700;
    width: 34px;
    padding: 5px 2px;
    font-size: 11px;
  }
  table.report td.mark-present { background: #ecfdf3; color: #027a48; }
  table.report td.mark-late { background: #ffedd5; color: #c2410c; }
  table.report td.mark-absent { background: #fee4e2; color: #b42318; }
  table.report th.mark-holiday {
    color: ${MUTED};
    font-weight: 600;
  }
  table.report td.mark-holiday {
    color: transparent;
    background-color: #eef1f5;
    background-image: repeating-linear-gradient(
      -45deg,
      #eef1f5,
      #eef1f5 2px,
      #d5dbe3 2px,
      #d5dbe3 4px
    );
  }
  .empty {
    padding: 10px;
    color: ${MUTED};
    font-size: 12px;
    border: 1px dashed ${LINE};
    border-radius: 6px;
  }
  .signature-wrap {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    border-top: 1.5px solid ${LINE};
    padding: 24px 0 10px;
    margin-top: auto;
  }
  .signature-col {
    text-align: center;
    min-width: 220px;
  }
  .signature-label {
    font-size: 13px;
    color: ${MUTED};
    margin-bottom: 26px;
  }
  .signature-name {
    font-size: 15px;
    font-weight: 700;
    color: ${INK};
    border-top: 1px solid #d9e0e8;
    padding-top: 8px;
  }
`;

@Injectable({ providedIn: 'root' })
export class AttendancePdfService {
  private logoDataUrl = '';
  private bannerDataUrl = '';

  async export(input: AttendancePdfExportInput): Promise<void> {
    if (!input.classes.length) {
      return;
    }

    await this.ensureLogos();

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const margin = 24;
    let pageStarted = false;
    let globalPageIndex = 0;

    const allPages: { html: string; className: string }[] = [];
    const useCombinedAbsent = !!input.combined || (!!input.absentOnly && input.classes.length > 1);

    if (useCombinedAbsent) {
      for (const html of this.buildCombinedAbsentPageHtmls(input)) {
        allPages.push({ html, className: 'all-classes' });
      }
    } else {
      for (const classReport of input.classes) {
        const pages = this.buildClassPageHtmls(input, classReport);
        for (const html of pages) {
          allPages.push({ html, className: classReport.className });
        }
      }
    }

    const totalPages = Math.max(1, allPages.length);

    for (const page of allPages) {
      const canvas = await this.rasterizeHtml(page.html, input.isArabic);
      globalPageIndex += 1;
      if (pageStarted) {
        pdf.addPage();
      }
      this.drawCanvasPage(canvas, pdf, margin, globalPageIndex, totalPages);
      pageStarted = true;
    }

    const dateSlug = input.date || 'report';
    const classSlug = input.classes.length === 1
      ? this.slug(input.classes[0].className)
      : 'all-classes';
    const kind = input.absentOnly ? 'absent' : 'attendance';
    this.savePdf(pdf, `${kind}-${dateSlug}-${classSlug}.pdf`);
  }

  private drawCanvasPage(
    canvas: HTMLCanvasElement,
    pdf: jsPDF,
    margin: number,
    pageIndex: number,
    totalPages: number,
  ): void {
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const footerReserve = 22;
    const maxImgWidth = pageW - margin * 2;
    const maxImgHeight = pageH - margin * 2 - footerReserve;
    const scale = Math.min(maxImgWidth / canvas.width, maxImgHeight / canvas.height);
    const drawWidth = canvas.width * scale;
    const drawHeight = canvas.height * scale;
    const x = margin + (maxImgWidth - drawWidth) / 2;

    const imgData = canvas.toDataURL('image/png');
    pdf.addImage(imgData, 'PNG', x, margin, drawWidth, drawHeight, undefined, 'NONE');
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(91, 102, 117);
    pdf.text(`${pageIndex}/${totalPages}`, pageW / 2, pageH - Math.max(10, margin * 0.55), {
      align: 'center',
    });
  }

  private resolveAssetUrl(relativePath: string): string {
    return new URL(relativePath, document.baseURI).href;
  }

  private async toDataUrl(src: string): Promise<string> {
    const response = await fetch(src);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error(`Failed to read image: ${src}`));
      reader.readAsDataURL(blob);
    });
  }

  private async ensureLogos(): Promise<void> {
    if (this.logoDataUrl && this.bannerDataUrl) {
      return;
    }
    const [logo, banner] = await Promise.all([
      this.toDataUrl(this.resolveAssetUrl('assets/weekly-plan/school-logo.png')),
      this.toDataUrl(this.resolveAssetUrl('assets/weekly-plan/year-banner.jpg')),
    ]);
    this.logoDataUrl = logo;
    this.bannerDataUrl = banner;
  }

  private chunkRows<T>(rows: T[], size: number): T[][] {
    if (!rows.length) {
      return [[]];
    }
    const chunks: T[][] = [];
    for (let i = 0; i < rows.length; i += size) {
      chunks.push(rows.slice(i, i + size));
    }
    return chunks;
  }

  /**
   * One combined absent report: filter absents only, stack class sections in one flow.
   * Dense 2-column lists so many classes share pages (fast, not 1 PDF page per class).
   */
  private buildCombinedAbsentPageHtmls(input: AttendancePdfExportInput): string[] {
    const { labels, isArabic } = input;
    const nameOf = (row: AttendanceReportRow) => {
      if (isArabic) {
        return row.studentNameAr || row.studentNameEn || '—';
      }
      return row.studentNameEn || row.studentNameAr || '—';
    };

    const classReports = input.classes
      .map(item => ({
        className: item.className || labels.noClass,
        totalInClass: item.total || 0,
        absent: item.absent || [],
      }))
      .filter(item => item.absent.length > 0);

    const totalAbsent = classReports.reduce((sum, item) => sum + item.absent.length, 0);
    const totalStudents = classReports.reduce((sum, item) => sum + (item.totalInClass || item.absent.length), 0);
    const absenceRate = totalStudents ? Math.round((totalAbsent / totalStudents) * 100) : 0;
    const allClassesLabel = labels.allClasses || labels.noClass;
    const classesLabel = labels.classesWithAbsent || labels.classLabel;

    type ClassChunk = {
      className: string;
      rows: AttendanceReportRow[];
      offset: number;
      total: number;
      continued: boolean;
    };

    // 2-column list ≈ half a slot per name; keep sections packed tightly.
    const firstPageNameSlots = 56;
    const nextPageNameSlots = 72;
    const headerCost = 2;
    const pagesChunks: ClassChunk[][] = [];
    let current: ClassChunk[] = [];
    let used = 0;
    let pageCapacity = firstPageNameSlots;

    const flush = () => {
      if (!current.length) {
        return;
      }
      pagesChunks.push(current);
      current = [];
      used = 0;
      pageCapacity = nextPageNameSlots;
    };

    for (const cls of classReports) {
      let offset = 0;
      while (offset < cls.absent.length) {
        let remaining = pageCapacity - used;
        if (remaining <= headerCost) {
          flush();
          remaining = pageCapacity - used;
        }
        const take = Math.min(cls.absent.length - offset, Math.max(1, remaining - headerCost));
        current.push({
          className: cls.className,
          rows: cls.absent.slice(offset, offset + take),
          offset,
          total: cls.absent.length,
          continued: offset > 0,
        });
        used += headerCost + take;
        offset += take;
        if (used >= pageCapacity) {
          flush();
        }
      }
    }
    flush();

    if (!pagesChunks.length) {
      pagesChunks.push([]);
    }

    return pagesChunks.map((chunks, pageIndex) => {
      const parts: string[] = [];
      if (pageIndex === 0) {
        parts.push(`
          <div class="summary">
            <div class="summary-item total">
              <span class="label">${this.escapeHtml(classesLabel)}</span>
              <span class="value">${classReports.length}</span>
            </div>
            <div class="summary-item absent">
              <span class="label">${this.escapeHtml(labels.absent)}</span>
              <span class="value">${totalAbsent}</span>
            </div>
            <div class="summary-item rate-bad">
              <span class="label">${this.escapeHtml(labels.absenceRate)}</span>
              <span class="value">${absenceRate}%</span>
            </div>
          </div>`);
      }

      for (const chunk of chunks) {
        const cont = chunk.continued ? ' …' : '';
        const items = chunk.rows.map((row, index) => `
          <li>
            <span class="num">${chunk.offset + index + 1}.</span>
            <span class="name">${this.escapeHtml(nameOf(row))}</span>
          </li>`).join('');
        parts.push(`
          <section class="class-block">
            <div class="class-block-title">
              ${this.escapeHtml(chunk.className)}
              <span class="count">(${chunk.total}${cont})</span>
            </div>
            <ul class="absent-list">${items}</ul>
          </section>`);
      }

      if (pageIndex === pagesChunks.length - 1) {
        parts.push(this.buildSignatureHtml(input));
      }

      return this.wrapSheet(
        input,
        {
          classId: 'all',
          className: allClassesLabel,
          total: totalStudents,
          attended: [],
          late: [],
          absent: [],
        },
        parts.join(''),
        pageIndex > 0,
      );
    });
  }

  private buildClassPageHtmls(
    input: AttendancePdfExportInput,
    classReport: AttendancePdfClassReport,
  ): string[] {
    const { labels, isArabic } = input;
    const nameOf = (row: AttendanceReportRow) => {
      if (isArabic) {
        return row.studentNameAr || row.studentNameEn || '—';
      }
      return row.studentNameEn || row.studentNameAr || '—';
    };

    const total = classReport.total || (
      classReport.attended.length + classReport.absent.length
    );
    const attendedCount = classReport.attended.length;
    const absentCount = classReport.absent.length;
    const lateCount = input.lateEnabled === false
      ? 0
      : (classReport.late?.length
        ?? classReport.attended.filter(row => row.checkIn?.status === 'late').length);
    const attendanceRate = total ? Math.round((attendedCount / total) * 100) : 0;
    const absenceRate = total ? Math.round((absentCount / total) * 100) : 0;

    const attendedSize = 16;
    const absentSize = 22;

    type PageSpec = {
      summary: boolean;
      attended?: { rows: AttendanceReportRow[]; offset: number };
      absent?: { rows: AttendanceReportRow[]; offset: number };
      signature: boolean;
      continued: boolean;
    };

    const specs: PageSpec[] = [];

    if (input.absentOnly) {
      const absentPages = classReport.absent.length
        ? this.chunkRows(classReport.absent, absentSize)
        : [[]];

      let absentOffset = 0;
      for (let i = 0; i < absentPages.length; i += 1) {
        const rows = absentPages[i];
        specs.push({
          summary: i === 0,
          absent: { rows, offset: absentOffset },
          signature: false,
          continued: i > 0,
        });
        absentOffset += rows.length;
      }
    } else {
      const attendedPages: AttendanceReportRow[][] = [];
      if (classReport.attended.length) {
        for (let i = 0; i < classReport.attended.length; i += attendedSize) {
          attendedPages.push(classReport.attended.slice(i, i + attendedSize));
        }
      }

      let attendedOffset = 0;
      for (let i = 0; i < attendedPages.length; i += 1) {
        const rows = attendedPages[i];
        specs.push({
          summary: specs.length === 0,
          attended: { rows, offset: attendedOffset },
          signature: false,
          continued: specs.length > 0,
        });
        attendedOffset += rows.length;
      }

      // If nobody attended, still show an empty attended section on the first page with summary
      if (!attendedPages.length) {
        specs.push({
          summary: true,
          attended: { rows: [], offset: 0 },
          signature: false,
          continued: false,
        });
      }

      const absentPages = classReport.absent.length
        ? this.chunkRows(classReport.absent, absentSize)
        : [];

      let absentOffset = 0;
      for (let i = 0; i < absentPages.length; i += 1) {
        const rows = absentPages[i];
        specs.push({
          summary: false,
          absent: { rows, offset: absentOffset },
          signature: false,
          continued: true,
        });
        absentOffset += rows.length;
      }
    }

    if (!specs.length) {
      specs.push({ summary: true, signature: true, continued: false });
    }
    specs[specs.length - 1].signature = true;

    return specs.map(spec => {
      const parts: string[] = [];
      if (spec.summary) {
        parts.push(this.buildSummaryHtml(labels, {
          total,
          attendedCount,
          lateCount,
          absentCount,
          attendanceRate,
          absenceRate,
        }, input.lateEnabled !== false));
      }
      if (spec.attended) {
        parts.push(this.buildSectionHtml(
          'attended',
          labels.attended,
          spec.attended.rows,
          labels,
          nameOf,
          true,
          spec.attended.offset,
          attendedCount,
          input.lateEnabled !== false,
        ));
      }
      if (spec.absent) {
        parts.push(this.buildSectionHtml(
          'absent',
          labels.absent,
          spec.absent.rows,
          labels,
          nameOf,
          false,
          spec.absent.offset,
          absentCount,
        ));
      }
      if (spec.signature) {
        parts.push(this.buildSignatureHtml(input));
      }
      return this.wrapSheet(input, classReport, parts.join(''), spec.continued);
    });
  }

  private wrapSheet(
    input: AttendancePdfExportInput,
    classReport: AttendancePdfClassReport,
    bodyHtml: string,
    _continued: boolean,
  ): string {
    const { labels, isArabic } = input;
    const dir = isArabic ? 'rtl' : 'ltr';

    return `
      <div id="sheet" dir="${dir}">
        <div class="sheet-head">
          <div class="sheet-logos">
            <img src="${this.logoDataUrl}" alt="شعار المدرسة">
            <img src="${this.bannerDataUrl}" alt="بالميثاق نبلغ الآفاق">
          </div>
          <div class="sheet-head-text" dir="${dir}">
            <div class="sheet-school">${this.escapeHtml(input.schoolName || '')}</div>
            <div class="sheet-title">${this.escapeHtml(labels.title)}</div>
            <div class="sheet-meta">
              ${this.escapeHtml(labels.date)}: ${this.escapeHtml(this.fmtDate(input.date, isArabic))}
              &nbsp;·&nbsp;
              ${this.escapeHtml(labels.classLabel)}: ${this.escapeHtml(classReport.className || labels.noClass)}
            </div>
          </div>
        </div>
        <div class="sheet-body">
          ${bodyHtml}
        </div>
      </div>`;
  }

  private buildSummaryHtml(
    labels: AttendancePdfLabels,
    stats: {
      total: number;
      attendedCount: number;
      lateCount: number;
      absentCount: number;
      attendanceRate: number;
      absenceRate: number;
    },
    lateEnabled = true,
  ): string {
    const lateItem = lateEnabled ? `
        <div class="summary-item late">
          <span class="label">${this.escapeHtml(labels.late)}</span>
          <span class="value">${stats.lateCount}</span>
        </div>` : '';
    return `
      <div class="summary">
        <div class="summary-item total">
          <span class="label">${this.escapeHtml(labels.total)}</span>
          <span class="value">${stats.total}</span>
        </div>
        <div class="summary-item attended">
          <span class="label">${this.escapeHtml(labels.attended)}</span>
          <span class="value">${stats.attendedCount}</span>
        </div>
        ${lateItem}
        <div class="summary-item absent">
          <span class="label">${this.escapeHtml(labels.absent)}</span>
          <span class="value">${stats.absentCount}</span>
        </div>
        <div class="summary-item rate-ok">
          <span class="label">${this.escapeHtml(labels.attendanceRate)}</span>
          <span class="value">${stats.attendanceRate}%</span>
        </div>
        <div class="summary-item rate-bad">
          <span class="label">${this.escapeHtml(labels.absenceRate)}</span>
          <span class="value">${stats.absenceRate}%</span>
        </div>
      </div>`;
  }

  private buildSignatureHtml(input: AttendancePdfExportInput): string {
    const principalName = input.headName?.trim() || '';
    const sectionHeadName = input.sectionHeadName?.trim() || '';
    if (!principalName && !sectionHeadName) {
      return '';
    }

    const sectionColumn = sectionHeadName ? `
      <div class="signature-col">
        <div class="signature-label">رئيس القسم</div>
        <div class="signature-name">${this.escapeHtml(sectionHeadName)}</div>
      </div>` : '<div class="signature-col"></div>';

    const principalColumn = principalName ? `
      <div class="signature-col">
        <div class="signature-label">مديرة المدرسة</div>
        <div class="signature-name">${this.escapeHtml(principalName)}</div>
      </div>` : '<div class="signature-col"></div>';

    return `
      <div class="signature-wrap">
        ${sectionColumn}
        ${principalColumn}
      </div>`;
  }

  private buildSectionHtml(
    kind: 'attended' | 'absent',
    title: string,
    rows: AttendanceReportRow[],
    labels: AttendancePdfLabels,
    nameOf: (row: AttendanceReportRow) => string,
    withTimes: boolean,
    startIndex = 0,
    sectionTotal?: number,
    lateEnabled = true,
  ): string {
    const total = sectionTotal ?? rows.length;
    const countLabel = `${title} (${total})`;
    if (!rows.length && startIndex === 0) {
      return `
        <section class="section">
          <div class="section-title ${kind}">${this.escapeHtml(countLabel)}</div>
          <div class="empty">${this.escapeHtml(labels.emptySection)}</div>
        </section>`;
    }
    if (!rows.length) {
      return '';
    }

    const head = withTimes
      ? (lateEnabled
        ? `<tr>
          <th class="num">#</th>
          <th class="name">${this.escapeHtml(labels.name)}</th>
          <th class="time">${this.escapeHtml(labels.checkIn)}</th>
          <th class="timing">${this.escapeHtml(labels.timing)}</th>
          <th class="time">${this.escapeHtml(labels.checkOut)}</th>
        </tr>`
        : `<tr>
          <th class="num">#</th>
          <th class="name">${this.escapeHtml(labels.name)}</th>
          <th class="time">${this.escapeHtml(labels.checkIn)}</th>
          <th class="time">${this.escapeHtml(labels.checkOut)}</th>
        </tr>`)
      : `<tr>
          <th class="num">#</th>
          <th class="name">${this.escapeHtml(labels.name)}</th>
        </tr>`;

    const body = rows.map((row, index) => {
      const isLate = lateEnabled && row.checkIn?.status === 'late';
      const rowClass = isLate ? ' class="row-late"' : '';
      const num = startIndex + index + 1;
      if (!withTimes) {
        return `<tr${rowClass}>
          <td class="num">${num}</td>
          <td class="name">${this.escapeHtml(nameOf(row))}</td>
        </tr>`;
      }
      if (!lateEnabled) {
        return `<tr>
          <td class="num">${num}</td>
          <td class="name">${this.escapeHtml(nameOf(row))}</td>
          <td class="time">${this.escapeHtml(this.fmtTime(row.checkIn?.at))}</td>
          <td class="time">${this.escapeHtml(this.fmtTime(row.checkOut?.at))}</td>
        </tr>`;
      }
      return `<tr${rowClass}>
        <td class="num">${num}</td>
        <td class="name">${this.escapeHtml(nameOf(row))}</td>
        <td class="time">${this.escapeHtml(this.fmtTime(row.checkIn?.at))}</td>
        <td class="timing">${this.escapeHtml(this.timingLabel(row.checkIn?.status, labels))}</td>
        <td class="time">${this.escapeHtml(this.fmtTime(row.checkOut?.at))}</td>
      </tr>`;
    }).join('');

    return `
      <section class="section">
        <div class="section-title ${kind}">${this.escapeHtml(countLabel)}</div>
        <table class="report">
          <thead>${head}</thead>
          <tbody>${body}</tbody>
        </table>
      </section>`;
  }

  private timingLabel(status: AttendanceStatus | null | undefined, labels: AttendancePdfLabels): string {
    if (status === 'early') return labels.statusEarly;
    if (status === 'late') return labels.statusLate;
    if (status === 'on_time') return labels.statusOnTime;
    return '—';
  }

  private fmtTime(at?: number | null): string {
    if (!at) {
      return '—';
    }
    return new Date(at).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }

  private fmtDate(value: string, isArabic: boolean): string {
    if (!value) {
      return '—';
    }
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleDateString(isArabic ? 'ar-EG' : 'en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  private escapeHtml(value: string): string {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private slug(value: string): string {
    return String(value || 'class')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^\w\u0600-\u06FF-]+/g, '')
      .slice(0, 40) || 'class';
  }

  private wrapDocument(innerHtml: string, isArabic: boolean, pageWidthPx = PAGE_WIDTH_PX): string {
    const css = SHEET_CSS.replace(
      `width: ${PAGE_WIDTH_PX}px`,
      `width: ${pageWidthPx}px`,
    );
    return `<!DOCTYPE html>
<html lang="${isArabic ? 'ar' : 'en'}" dir="${isArabic ? 'rtl' : 'ltr'}">
<head>
  <meta charset="utf-8">
  <link rel="stylesheet" href="/assets/fonts/fonts.css">
  <style>${css}</style>
</head>
<body>${innerHtml}</body>
</html>`;
  }

  private async waitFrames(targetWindow: Window = window): Promise<void> {
    const fonts = targetWindow.document?.fonts;
    if (fonts) {
      try {
        await fonts.load('400 13px "Noto Naskh Arabic"');
        await fonts.load('700 13px "Noto Naskh Arabic"');
        await fonts.ready;
      } catch {
        // Capture still runs if the face is unavailable.
      }
    }
    await new Promise<void>(resolve => {
      targetWindow.requestAnimationFrame(() => {
        targetWindow.requestAnimationFrame(() => resolve());
      });
    });
  }

  private async rasterizeHtml(
    innerHtml: string,
    isArabic: boolean,
    pageWidthPx = PAGE_WIDTH_PX,
  ): Promise<HTMLCanvasElement> {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.position = 'fixed';
    iframe.style.left = '0';
    iframe.style.top = '0';
    iframe.style.width = `${pageWidthPx}px`;
    iframe.style.height = '240px';
    iframe.style.opacity = '0.01';
    iframe.style.pointerEvents = 'none';
    iframe.style.border = '0';
    iframe.style.zIndex = '-1';
    iframe.style.background = '#ffffff';

    const loaded = new Promise<void>(resolve => {
      iframe.onload = () => resolve();
    });
    iframe.srcdoc = this.wrapDocument(innerHtml, isArabic, pageWidthPx);
    document.body.appendChild(iframe);
    await loaded;

    try {
      const doc = iframe.contentDocument;
      const win = iframe.contentWindow;
      if (!doc || !win) {
        throw new Error('Failed to prepare PDF page');
      }

      if (doc.fonts?.ready) {
        await doc.fonts.ready;
      }
      await this.waitFrames(win);

      const sheet = doc.getElementById('sheet');
      if (!sheet) {
        throw new Error('Failed to build PDF sheet');
      }

      iframe.style.height = `${Math.max(sheet.scrollHeight + 48, 200)}px`;
      await this.waitFrames(win);

      return await toCanvas(sheet, {
        pixelRatio: 3,
        backgroundColor: '#ffffff',
        cacheBust: true,
        skipFonts: true,
        width: sheet.scrollWidth,
        height: sheet.scrollHeight,
        style: {
          fontFamily: ARABIC_FONT,
          color: INK,
          background: '#ffffff',
        },
      });
    } finally {
      iframe.remove();
    }
  }

  private savePdf(pdf: jsPDF, filename: string): void {
    const blob = pdf.output('blob');
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => {
      try {
        window.open(url, '_blank');
      } catch {
        /* ignore popup blockers */
      }
    }, 250);
  }

  async exportRange(input: AttendanceRangePdfExportInput): Promise<void> {
    if (!input.classes.length) {
      return;
    }

    await this.ensureLogos();

    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'pt',
      format: 'a4',
    });
    const margin = 18;
    let pageStarted = false;
    let globalPageIndex = 0;
    const sheetWidth = LANDSCAPE_PAGE_WIDTH_PX;

    const allPages: string[] = [];
    for (const classReport of input.classes) {
      allPages.push(...this.buildRangeClassPageHtmls(input, classReport));
    }

    const totalPages = Math.max(1, allPages.length);
    for (const html of allPages) {
      const canvas = await this.rasterizeHtml(html, input.isArabic, sheetWidth);
      globalPageIndex += 1;
      if (pageStarted) {
        pdf.addPage();
      }
      this.drawCanvasPage(canvas, pdf, margin, globalPageIndex, totalPages);
      pageStarted = true;
    }

    this.savePdf(pdf, `attendance-range-${input.from}_${input.to}.pdf`);
  }

  private buildRangeClassPageHtmls(
    input: AttendanceRangePdfExportInput,
    classReport: AttendanceRangePdfClassReport,
  ): string[] {
    // Same student count on every page so page 1 is utilized like the rest.
    const rowsPerPage = 16;
    const pages: AttendanceRangePdfRow[][] = [];
    if (classReport.rows.length) {
      for (let i = 0; i < classReport.rows.length; i += rowsPerPage) {
        pages.push(classReport.rows.slice(i, i + rowsPerPage));
      }
    } else {
      pages.push([]);
    }

    let offset = 0;
    return pages.map((chunk, index) => {
      const isFirst = index === 0;
      const isLast = index === pages.length - 1;
      const body = [
        isFirst ? this.buildRangeSummaryHtml(input) : '',
        this.buildRangeTableHtml(input, classReport, chunk, offset),
        isLast ? this.buildSignatureHtml({
          headName: input.headName,
          sectionHeadName: input.sectionHeadName,
        } as AttendancePdfExportInput) : '',
      ].join('');
      offset += chunk.length;
      return this.wrapRangeSheet(input, classReport, body, !isFirst);
    });
  }

  private wrapRangeSheet(
    input: AttendanceRangePdfExportInput,
    classReport: AttendanceRangePdfClassReport,
    bodyHtml: string,
    _continued: boolean,
  ): string {
    const { labels, isArabic } = input;
    const dir = isArabic ? 'rtl' : 'ltr';
    const arrow = isArabic ? '←' : '→';
    const rangeText = `${this.fmtDate(input.from, isArabic)} ${arrow} ${this.fmtDate(input.to, isArabic)}`;

    return `
      <div id="sheet" class="landscape" dir="${dir}">
        <div class="sheet-head">
          <div class="sheet-logos">
            <img src="${this.logoDataUrl}" alt="">
            <img src="${this.bannerDataUrl}" alt="">
          </div>
          <div class="sheet-head-text" dir="${dir}">
            <div class="sheet-school">${this.escapeHtml(input.schoolName || '')}</div>
            <div class="sheet-title">${this.escapeHtml(labels.title)}</div>
            <div class="sheet-meta">
              ${this.escapeHtml(labels.dateRange)}: ${this.escapeHtml(rangeText)}
              &nbsp;·&nbsp;
              ${this.escapeHtml(labels.classLabel)}: ${this.escapeHtml(classReport.className || labels.noClass)}
            </div>
          </div>
        </div>
        <div class="sheet-body">
          ${bodyHtml}
        </div>
      </div>`;
  }

  private buildRangeSummaryHtml(input: AttendanceRangePdfExportInput): string {
    const { labels, summary } = input;
    const lateEnabled = input.lateEnabled !== false;
    const lateItem = lateEnabled ? `
        <div class="summary-item late">
          <span class="label">${this.escapeHtml(labels.late)}</span>
          <span class="value">${summary.late}</span>
        </div>` : '';
    return `
      <div class="summary">
        <div class="summary-item total">
          <span class="label">${this.escapeHtml(labels.totalStudents)}</span>
          <span class="value">${summary.totalStudents}</span>
        </div>
        <div class="summary-item total">
          <span class="label">${this.escapeHtml(labels.dayCount)}</span>
          <span class="value">${summary.dayCount}</span>
        </div>
        <div class="summary-item rate-ok">
          <span class="label">${this.escapeHtml(labels.attendanceRate)}</span>
          <span class="value">${summary.attendanceRate}%</span>
        </div>
        <div class="summary-item rate-bad">
          <span class="label">${this.escapeHtml(labels.absenceRate)}</span>
          <span class="value">${summary.absenceRate}%</span>
        </div>
        ${lateItem}
      </div>`;
  }

  private buildRangeTableHtml(
    input: AttendanceRangePdfExportInput,
    classReport: AttendanceRangePdfClassReport,
    rows: AttendanceRangePdfRow[],
    startIndex: number,
  ): string {
    const { labels, days } = input;
    const weekdayHolidays = new Set(input.holidayWeekdays || []);
    const namedHolidays = new Set(input.holidayDates || []);
    if (!classReport.rows.length) {
      return `
        <section class="section">
          <div class="empty">${this.escapeHtml(labels.emptySection)}</div>
        </section>`;
    }
    if (!rows.length) {
      return '';
    }

    const showDays = days.length > 0 && days.length <= 10;
    const dayHeads = showDays
      ? days.map(day => {
          const holiday = this.isHolidayYmd(day, weekdayHolidays, namedHolidays);
          return `<th class="mark${holiday ? ' mark-holiday' : ''}">${this.escapeHtml(this.fmtDayShort(day))}</th>`;
        }).join('')
      : '';

    const lateEnabled = input.lateEnabled !== false;
    const lateHead = lateEnabled
      ? `<th class="metric">${this.escapeHtml(labels.lateDays)}</th>`
      : '';
    const head = `<tr>
      <th class="num">#</th>
      <th class="name">${this.escapeHtml(labels.name)}</th>
      ${dayHeads}
      <th class="metric">${this.escapeHtml(labels.presentDays)}</th>
      <th class="metric">${this.escapeHtml(labels.absentDays)}</th>
      ${lateHead}
      <th class="metric">${this.escapeHtml(labels.rate)}</th>
      <th class="metric">${this.escapeHtml(labels.absenceRate)}</th>
    </tr>`;

    const body = rows.map((row, index) => {
      const dayCells = showDays
        ? days.map(day => {
            const holiday = this.isHolidayYmd(day, weekdayHolidays, namedHolidays)
              || row.dayMarks?.[day] === 'holiday';
            if (holiday) {
              return `<td class="mark mark-holiday">&nbsp;</td>`;
            }
            let mark = row.dayMarks?.[day] || 'absent';
            if (!lateEnabled && mark === 'late') {
              mark = 'present';
            }
            const letter = mark === 'late'
              ? labels.markLate
              : mark === 'present'
                ? labels.markPresent
                : labels.markAbsent;
            return `<td class="mark mark-${mark}">${this.escapeHtml(letter)}</td>`;
          }).join('')
        : '';
      const lateCell = lateEnabled ? `<td class="metric">${row.lateDays}</td>` : '';
      const absenceRate = Math.max(0, Math.min(100, 100 - (row.attendanceRate || 0)));
      return `<tr>
        <td class="num">${startIndex + index + 1}</td>
        <td class="name">${this.escapeHtml(row.studentName)}</td>
        ${dayCells}
        <td class="metric">${row.presentDays}</td>
        <td class="metric">${row.absentDays}</td>
        ${lateCell}
        <td class="metric">${row.attendanceRate}%</td>
        <td class="metric">${absenceRate}%</td>
      </tr>`;
    }).join('');

    return `
      <section class="section">
        <table class="report">
          <thead>${head}</thead>
          <tbody>${body}</tbody>
        </table>
      </section>`;
  }

  /** Compact single-line day label for narrow mark columns (e.g. 19/9). */
  private fmtDayShort(value: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) {
      return value.slice(5) || value;
    }
    return `${Number(match[3])}/${Number(match[2])}`;
  }

  /** UAE has no DST; noon Dubai == 08:00 UTC same calendar day. */
  private isHolidayYmd(
    value: string,
    weekdayHolidays: Set<number>,
    namedHolidays: Set<string>,
  ): boolean {
    if (namedHolidays.has(value)) {
      return true;
    }
    if (!weekdayHolidays.size) {
      return false;
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) {
      return false;
    }
    const weekday = new Date(`${match[1]}-${match[2]}-${match[3]}T08:00:00Z`).getUTCDay();
    return weekdayHolidays.has(weekday);
  }
}
